import { Zap } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateEnergy } from "./formulas";
import type { EnergyInputs } from "./types";

type EnergyCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  inputs: EnergyInputs;
  inputText: Record<keyof EnergyInputs, string>;
  onInputTextChange: (key: keyof EnergyInputs, value: string) => void;
};

function displayLengthCm(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value / 2.54;
}

function displayEnergyPerLength(value: number, unitSystem: UnitSystem) {
  return unitSystem === "metric" ? value : value * 2.54;
}

export function EnergyCalculator({ language, unitSystem, inputs, inputText, onInputTextChange }: EnergyCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateEnergy(inputs), [inputs]);
  const lengthUnit = unitSystem === "metric" ? "cm" : "in";
  const energyUnit = unitSystem === "metric" ? "kJ/cm" : "kJ/in";

  return (
    <section className="calculator-panel" aria-label={t.modules.energy}>
      <div className="calculator-heading">
        <div>
          <h2>{t.modules.energy}</h2>
          <span>{t.modules.energyDescription}</span>
        </div>
      </div>

      <div className="energy-method-layout">
        <div className="energy-method-grid">
          <section className="energy-method-panel energy-method-impulse">
            <div className="section-label">{t.energy.impulseMode}</div>
            <div className="energy-method-content">
              <div className="input-grid">
                <NumericInput
                  label={t.energy.impulseEnergy}
                  shortLabel="IE"
                  unit="kJ"
                  value={inputs.impulseEnergy}
                  textValue={inputText.impulseEnergy}
                  decimals={1}
                  locale={locale}
                  onChange={(value) => onInputTextChange("impulseEnergy", value)}
                />
                <NumericInput
                  label={t.energy.weldLength}
                  shortLabel={t.timeSpeed.lengthShort}
                  unit={lengthUnit}
                  value={displayLengthCm(inputs.weldLength, unitSystem)}
                  textValue={inputText.weldLength}
                  decimals={unitSystem === "metric" ? 1 : 2}
                  locale={locale}
                  onChange={(value) => onInputTextChange("weldLength", value)}
                />
              </div>
            <div className="energy-method-result result-zone">
                <span>{t.energy.impulseMethod}</span>
                <strong>
                  {formatNumber(displayEnergyPerLength(result.energyFromImpulse, unitSystem), 2, locale)}
                  <b>{energyUnit}</b>
                </strong>
                <code>IE / length</code>
              </div>
            </div>
          </section>

          <section className="energy-method-panel energy-method-power">
            <div className="section-label">{t.energy.powerMode}</div>
            <div className="energy-method-content">
              <div className="input-grid">
                <NumericInput
                  label={t.energy.power}
                  shortLabel="IP"
                  unit="kW"
                  value={inputs.power}
                  textValue={inputText.power}
                  decimals={3}
                  locale={locale}
                  onChange={(value) => onInputTextChange("power", value)}
                />
                <NumericInput
                  label={t.inputs.travelSpeed}
                  shortLabel="vs"
                  unit={unitFor("travelSpeed", unitSystem)}
                  value={toDisplayValue("travelSpeed", inputs.travelSpeed, unitSystem)}
                  textValue={inputText.travelSpeed}
                  decimals={decimalsFor("travelSpeed", unitSystem)}
                  locale={locale}
                  onChange={(value) => onInputTextChange("travelSpeed", value)}
                />
              </div>
            <div className="energy-method-result result-zone">
                <span>{t.energy.powerMethod}</span>
                <strong>
                  {formatNumber(displayEnergyPerLength(result.energyFromPower, unitSystem), 2, locale)}
                  <b>{energyUnit}</b>
                </strong>
                <code>IP / vs * 60</code>
              </div>
            </div>
          </section>
        </div>

        <aside className="result-panel energy-compare-panel" aria-live="polite">
          <div className="result-header">
            <span>
              <Zap size={16} /> {t.energy.energyComparison}
            </span>
          </div>
          <div className="detail-grid energy-comparison-details">
            <div className="energy-comparison-row">
              <span>{t.energy.difference}</span>
              <strong>{formatNumber(displayEnergyPerLength(result.difference, unitSystem), 2, locale)} {energyUnit}</strong>
            </div>
            <div className="energy-comparison-row">
              <span>{t.energy.differencePercent}</span>
              <strong>{formatNumber(result.differencePercent, 1, locale)} %</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
